import { useEffect, useRef, useState } from "react";
import { CHAIN } from "@tonconnect/protocol";
import { TonConnectButton, useTonAddress, useTonConnectUI, useTonWallet } from "@tonconnect/ui-react";
import { type ProtocolPool, type WalletBroadcaster } from "@zkresistor/shared";
import {
  ASSETS,
  createClientRuntime,
  resetTransfers,
  useController,
  type ClientRuntime,
  type PrivateRuntime,
} from "@zkresistor/client";
import { Boot, BootFailure, Loading } from "./components/Status";
import { Docs } from "./Docs";
import { Footer } from "./Footer";
import { CatalogPage } from "./pages/CatalogPage";
import { CreatePoolPage } from "./pages/CreatePoolPage";
import { PoolPage, type TransferMode } from "./pages/PoolPage";

type View = "pools" | "create" | "pool" | "docs";

function createProofWorker(): Worker {
  return new Worker(new URL("./workers/proof.worker.ts", import.meta.url), { type: "module" });
}

function poolUrl(pool: ProtocolPool, mode: TransferMode): string {
  return `/?${new URLSearchParams({ pool: pool.id, mode }).toString()}`;
}

function initialView(): View {
  return window.location.pathname.replace(/\/+$/, "") === "/docs" ? "docs" : "pools";
}

export default function App() {
  const [tonConnect] = useTonConnectUI();
  const wallet = useTonWallet();
  const walletAddress = useTonAddress(true);
  const addressRef = useRef(walletAddress);
  const chainRef = useRef(wallet?.account.chain);
  const [runtime, setRuntime] = useState<ClientRuntime | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  addressRef.current = walletAddress;
  chainRef.current = wallet?.account.chain;

  useEffect(() => {
    const broadcaster: WalletBroadcaster = {
      currentAddress: () => addressRef.current || null,
      async send(message) {
        if (chainRef.current !== CHAIN.MAINNET) throw new Error("Connect a TON mainnet wallet");
        const result = await tonConnect.sendTransaction({
          validUntil: Math.floor(Date.now() / 1000) + 600,
          network: CHAIN.MAINNET,
          messages: [{
            address: message.address,
            amount: message.value.toString(),
            payload: message.payload.toBoc().toString("base64"),
          }],
        });
        return { boc: result.boc };
      },
    };

    void createClientRuntime(broadcaster, createProofWorker).then(setRuntime).catch((error) => {
      setBootError(error instanceof Error ? error.message : String(error));
    });
  }, [tonConnect]);

  if (bootError !== null) return <BootFailure error={bootError} />;
  if (runtime === null) return <Boot />;
  return <Workspace runtime={runtime} walletAddress={walletAddress} />;
}

function Workspace({ runtime, walletAddress }: { runtime: ClientRuntime; walletAddress: string }) {
  const catalog = useController(runtime.catalog);
  const createState = useController(runtime.createPool);
  const [view, setView] = useState<View>(initialView);
  const [mode, setMode] = useState<TransferMode>("deposit");
  const [selectedId, setSelectedId] = useState("");
  const [privateRuntime, setPrivateRuntime] = useState<PrivateRuntime | null>(null);
  const [privateError, setPrivateError] = useState<string | null>(null);
  const deepLinkApplied = useRef(false);

  useEffect(() => {
    void runtime.catalog.load();
  }, [runtime]);

  useEffect(() => {
    if (view === "docs" || deepLinkApplied.current || catalog.pools.length === 0) return;
    deepLinkApplied.current = true;
    const params = new URLSearchParams(window.location.search);
    const pool = catalog.pools.find((candidate) => candidate.id === params.get("pool"));
    if (pool === undefined) return;
    const requestedMode = params.get("mode") === "withdraw" ? "withdraw" : "deposit";
    setSelectedId(pool.id);
    setMode(requestedMode);
    setView("pool");
  }, [catalog.pools, view]);

  useEffect(() => {
    if (view !== "pool" || privateRuntime !== null) return;
    void runtime.privateRuntime().then(setPrivateRuntime).catch((error) => {
      setPrivateError(error instanceof Error ? error.message : String(error));
    });
  }, [privateRuntime, runtime, view]);

  const selected = catalog.pools.find((pool) => pool.id === selectedId) ?? null;
  const navigate = (next: "pools" | "create") => {
    resetTransfers(privateRuntime);
    setView(next);
    window.history.replaceState(null, "", "/");
  };
  const openPool = (pool: ProtocolPool) => {
    resetTransfers(privateRuntime);
    setSelectedId(pool.id);
    setMode("deposit");
    setView("pool");
    window.history.replaceState(null, "", poolUrl(pool, "deposit"));
  };
  const switchMode = (next: TransferMode, pool: ProtocolPool) => {
    if (next === mode) return;
    resetTransfers(privateRuntime);
    setMode(next);
    window.history.replaceState(null, "", poolUrl(pool, next));
  };

  return (
    <div className="app">
      <header className="topbar">
        <button type="button" className="brand" onClick={() => navigate("pools")}>
          <img src={ASSETS.zkresistor} alt="" />
          <span><b>ZK</b>Resistor</span>
        </button>
        <nav className="topnav" aria-label="Primary">
          <button
            type="button"
            className={view === "pools" || view === "pool" ? "active" : ""}
            onClick={() => navigate("pools")}
          >
            Pools
          </button>
          <button
            type="button"
            className={view === "create" ? "active" : ""}
            onClick={() => navigate("create")}
          >
            Create
          </button>
        </nav>
        <TonConnectButton />
      </header>
      <main className="main">
        {view === "pools" && <CatalogPage state={catalog} onSelect={openPool} />}
        {view === "create" && <CreatePoolPage state={createState} controller={runtime.createPool} />}
        {view === "docs" && <Docs />}
        {view === "pool" && selected === null && <p className="alert error">This Pool is not in the signed catalog.</p>}
        {view === "pool" && selected !== null && privateError !== null && <p className="alert error">{privateError}</p>}
        {view === "pool" && selected !== null && privateError === null && privateRuntime === null && (
          <Loading label="Loading private transfer tools" />
        )}
        {view === "pool" && selected !== null && privateRuntime !== null && (
          <PoolPage
            runtime={privateRuntime}
            mode={mode}
            pools={catalog.pools}
            selected={selected}
            walletAddress={walletAddress}
            onMode={switchMode}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}
